import { CopilotResponseAnnotations } from './copilot/copilotConversation';

export default class CompanyProfile {
  website: string;

  numberOfEmployees: number;

  industry: string;

  companyIntelligence: string;

  marketAndCustomers: string;

  technologyAndRegulatory: string;

  /** Whether the organization develops software itself or has it custom-built; undefined while unanswered. */
  scopeSoftwareDevelopment?: boolean;

  /** Whether the organization has a fixed office space, owned or rented; undefined while unanswered. */
  scopeFixedOffice?: boolean;

  annotations?: CopilotResponseAnnotations;

  constructor() {
    this.website = '';
    this.numberOfEmployees = 0;
    this.industry = '';
    this.companyIntelligence = '';
    this.marketAndCustomers = '';
    this.technologyAndRegulatory = '';
  }

  /**
   * A working copy.
   *
   * Written out rather than deep-cloned generically because `annotations.hasData` is an OWN
   * property (an arrow function assigned in the class body), and a structural clone hands back an
   * object that looks right until the first call to it.
   */
  clone(): CompanyProfile {
    const newItem = new CompanyProfile();
    newItem.website = this.website;
    newItem.numberOfEmployees = this.numberOfEmployees;
    newItem.industry = this.industry;
    newItem.companyIntelligence = this.companyIntelligence;
    newItem.marketAndCustomers = this.marketAndCustomers;
    newItem.technologyAndRegulatory = this.technologyAndRegulatory;
    newItem.scopeSoftwareDevelopment = this.scopeSoftwareDevelopment;
    newItem.scopeFixedOffice = this.scopeFixedOffice;

    if (this.annotations) {
      const annotations = new CopilotResponseAnnotations();
      annotations.citations = (this.annotations.citations ?? []).map((citation) => citation.clone());
      annotations.metadata = (this.annotations.metadata ?? []).map((pair) => ({ ...pair }));
      newItem.annotations = annotations;
    }

    return newItem;
  }

  isEqual(item: CompanyProfile): boolean {
    if (item.website !== this.website) return false;
    if (item.numberOfEmployees !== this.numberOfEmployees) return false;
    if (item.industry !== this.industry) return false;
    if (item.companyIntelligence !== this.companyIntelligence) return false;
    if (item.marketAndCustomers !== this.marketAndCustomers) return false;
    if (item.technologyAndRegulatory !== this.technologyAndRegulatory) return false;
    if (item.scopeSoftwareDevelopment !== this.scopeSoftwareDevelopment) return false;
    if (item.scopeFixedOffice !== this.scopeFixedOffice) return false;
    if (!this.annotationsEqual(item.annotations, this.annotations)) return false;

    return true;
  }

  private annotationsEqual(left?: CopilotResponseAnnotations, right?: CopilotResponseAnnotations): boolean {
    if ((left?.citations?.length || 0) !== (right?.citations?.length || 0)) return false;
    if ((left?.metadata?.length || 0) !== (right?.metadata?.length || 0)) return false;

    const leftCitations = left?.citations || [];
    const rightCitations = right?.citations || [];
    for (let i = 0; i < leftCitations.length; i++) {
      if (leftCitations[i].title !== rightCitations[i].title) return false;
      if (leftCitations[i].uri !== rightCitations[i].uri) return false;
    }

    const leftMetadata = left?.metadata || [];
    const rightMetadata = right?.metadata || [];
    for (let i = 0; i < leftMetadata.length; i++) {
      if (leftMetadata[i].key !== rightMetadata[i].key) return false;
      if (leftMetadata[i].value !== rightMetadata[i].value) return false;
    }

    return true;
  }
}
